import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FiHeart, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = (import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000") + "/api/activity";

const ActivitiesCountryCardsSection = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [cardsToShow, setCardsToShow] = useState(4);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`${API_BASE}/user/countries`);
        const countriesData = response.data?.data || [];

        const transformedData = countriesData.map((country) => ({
          id: country._id,
          name: country.name,
          code: country.code,
          image: country.image
            ? `${import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000"}/${country.image.replace(/\\/g, "/")}`
            : null,
          currency: country.currency,
          currencySymbol: country.currencySymbol,
          active: country.active,
        }));

        setCountries(transformedData);
      } catch (err) {
        console.error("API Fetch Failed:", err);
        setError("Failed to load countries");
        setCountries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const updateCardsToShow = () => {
      if (window.innerWidth < 640) setCardsToShow(1);
      else if (window.innerWidth < 1024) setCardsToShow(2);
      else setCardsToShow(4);
    };

    updateCardsToShow();
    window.addEventListener("resize", updateCardsToShow);

    return () => window.removeEventListener("resize", updateCardsToShow);
  }, []);

  useEffect(() => {
    if (!autoPlay || countries.length === 0) return;

    const interval = setInterval(() => {
      if (currentIndex < countries.length - cardsToShow) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setCurrentIndex(0);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [currentIndex, autoPlay, countries.length, cardsToShow]);

  const nextSlide = () => {
    if (currentIndex < countries.length - cardsToShow) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    } else {
      setCurrentIndex(countries.length - cardsToShow);
    }
  };

  const handleCardClick = (country) => {
    navigate("/activity", { state: { countryId: country.id } });
  };

  const visibleCountries = countries.slice(
    currentIndex,
    currentIndex + cardsToShow
  );

  if (loading && countries.length === 0) {
    return (
      <section className="py-8 md:py-10 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-10 bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-4 md:px-8">
        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-700">{error}</p>
          </div>
        )}

        {/* Section Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Explore Activities by Country
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Select a country to discover amazing activities, tours, and experiences.
            </p>
          </motion.div>
        </div>

        {/* Slider Container */}
        <div
          className="relative"
          onMouseEnter={() => setAutoPlay(false)}
          onMouseLeave={() => setAutoPlay(true)}
        >
          {/* Prev Button */}
          {countries.length > cardsToShow && (
            <button
              onClick={prevSlide}
              className="absolute -left-4 md:-left-9 top-1/2 transform -translate-y-1/2 z-10 bg-blue-500/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-blue-600 cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={currentIndex === 0}
            >
              <FiChevronLeft className="text-2xl text-gray-100" />
            </button>
          )}

          {/* Next Button */}
          {countries.length > cardsToShow && (
            <button
              onClick={nextSlide}
              className="absolute -right-4 md:-right-9 top-1/2 transform -translate-y-1/2 z-10 bg-blue-500/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-blue-600 cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={currentIndex >= countries.length - cardsToShow}
            >
              <FiChevronRight className="text-2xl text-gray-100" />
            </button>
          )}

          {/* Cards Grid */}
          {countries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                No countries available. Please add some countries from the admin panel.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {visibleCountries.map((country, index) => (
                  <motion.div
                    key={country.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{
                      scale: 1.03,
                      transition: { duration: 0.2 },
                    }}
                    className="cursor-pointer group"
                    onClick={() => handleCardClick(country)}
                  >
                    <div className="rounded-3xl overflow-hidden shadow-lg bg-white hover:shadow-2xl transition-all duration-300 h-full">
                      <div className="relative h-48 overflow-hidden">
                        {country.image ? (
                          <motion.img
                            src={country.image}
                            alt={country.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                            <span className="text-4xl font-bold text-white">
                              {country.code?.substring(0, 2) || country.name?.substring(0, 2)}
                            </span>
                          </div>
                        )}

                        {/* Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

                        {/* Country Code */}
                        <div className="absolute top-3 left-3 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-semibold text-white">
                          {country.code}
                        </div>

                        {/* Heart */}
                        <motion.button
                          className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-md hover:bg-white"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Add to favorites logic here
                          }}
                        >
                          <FiHeart className="text-gray-700 text-lg" />
                        </motion.button>

                        {/* Name */}
                        <div className="absolute bottom-3 left-3 text-white">
                          <h3 className="text-xl font-bold drop-shadow-lg">
                            {country.name}
                          </h3>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-600">
                            <span className="font-semibold">{country.currencySymbol}</span>
                            <span className="text-gray-500"> {country.currency}</span>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-3 py-2 whitespace-nowrap bg-blue-50 text-blue-600 font-semibold rounded-lg hover:bg-blue-100 transition-colors text-sm"
                          >
                            View Activities
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Dots */}
              {countries.length > cardsToShow && (
                <div className="flex justify-center mt-8 space-x-2">
                  {Array.from({
                    length: Math.max(1, countries.length - cardsToShow + 1),
                  }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentIndex
                          ? "w-8 bg-blue-600"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ActivitiesCountryCardsSection;
